<script setup lang="ts">
/* Bookings as <ItemRow>s: reference and status, then where and who, then
   when, how long and how much. `hide` drops what would only repeat the page
   it is on -- every booking on a berth's page is at that berth. */
import { computed } from 'vue'
import Badge from '@/components/ui/Badge.vue'
import ItemList from '@/components/ui/ItemList.vue'
import ItemRow from '@/components/ui/ItemRow.vue'
import { to } from '@/config/routes'
import {
  bookingState,
  bookingStateLabel,
  bookingStateTone,
  formatDateTime,
  formatDuration,
  formatEur,
} from '@/lib/bookingLabels'
import type { Booking } from '@/lib/types'

const props = withDefaults(defineProps<{ bookings: Booking[]; hide?: Array<'berth' | 'vessel'> }>(), {
  hide: () => [],
})

const rows = computed(() =>
  props.bookings.map((b) => {
    const state = bookingState(b)
    const where = [
      !props.hide.includes('berth') && b.berth ? `${b.berth.name}, ${b.berth.city}` : null,
      !props.hide.includes('vessel') && b.vessel ? b.vessel.name : null,
    ].filter(Boolean)
    return {
      b,
      state,
      where: where.join(' · '),
      when: `${formatDateTime(b.arrival)} · ${formatDuration(b.arrival, b.departure)}`,
    }
  }),
)
</script>

<template>
  <ItemList>
    <ItemRow
      v-for="r in rows"
      :key="r.b.id"
      :to="to.booking(r.b.id)"
      :muted="r.state === 'cancelled' || r.state === 'rejected'"
    >
      <template #title>{{ r.b.reference }}</template>
      <template #aside>
        <Badge :tone="bookingStateTone(r.state)">{{ bookingStateLabel(r.state) }}</Badge>
      </template>
      <span v-if="r.where">{{ r.where }}</span>
      <span>{{ r.when }} · <strong class="price">{{ formatEur(r.b.price_eur) }}</strong></span>
    </ItemRow>
  </ItemList>
</template>

<style scoped>
.price {
  color: var(--color-heading);
}
</style>
